// ═══════════════════════════════════════════════════════════════════════════
// drpYieldAdjust.js — RMA's DRP Yield Adjustment Factor (YAF) and the P28-1
// indemnity arithmetic, ONE rule for every caller: the DRP screen, the printed
// DRP summary, api/_drp-adm-sync.js (A00832 ingest), tools/drp-settlement-tieout.js
// and tools/check-drp-yaf.js.
//
// SOURCE — M13 Handbook P28-1 (Plan 83, DRP) and 25-DRP-CEE, replayed against
// RMA's own actuarial files and tied to the dollar on the paid book:
//   YAF                = round(actual yield ÷ expected yield, 4)
//   actual milk rev.   = round(round(actual price × covered lbs × YAF, 4) / 100, 0)
//   final milk rev.    = round(round(expected price × covered lbs, 4) / 100, 0)
//   revenue guarantee  = round(final milk revenue × coverage level, 0)
//   indemnity          = round(max(guarantee − actual revenue, 0) × share × protection factor, 0)
//
// WHICH YIELD — the expected AND actual yield are both RMA's, published per
// MILK YIELD ID in ADM A00832 (ADMDrpMilkYield). A milk yield ID is issued per
// (state × quarter) in each quarterly A00832 release, so ONE quarter carries a
// different ID — and a different expected yield — depending on WHEN it was sold.
// The ID an endorsement was sold under is named by RMA's A00833 daily price file
// for its own sales date: the row (field 42 = milk yield ID) whose expected
// Class III or IV price is the price the AIP wrote on the endorsement, among the
// IDs A00832 places in the policy's state. Nothing here guesses: an endorsement
// whose sales-date file is not loaded, or whose price matches no row of its
// state (or rows with DIFFERENT yields), is UNKNOWN — never silently YAF 1.0.
//
// ⚠ NOT BY QUARTER CODE. A00833 field 43 is the ADM ACTUAL PRICE ID, re-issued
// per release like the yield ID — not a quarter number. The +16144 formula in
// api/_drp-adm-sync.js (drpQuarterFromCode) fits it from RY2025's second release
// on, and is wrong for RY2025's first IDs 49-53 (measured: 50 = Q1 2025, the
// formula says 2023-Q3). The AIP's own price on the endorsement is the anchor.
//
// Measured 2026-10-20 (RMA directory listings): the actual yield for a quarter
// appears in the A00832 release ~3 weeks after the quarter ends — Q4 2025 on
// 2026-01-23, Q1 2026 on 2026-04-22, Q2 2026 on 2026-07-23 (Q3 2025 slipped to
// 2025-11-10). So "pending" names the late part of the month after the quarter.
// ═══════════════════════════════════════════════════════════════════════════
"use strict";

// RMA's rounding: half away from zero, with the float guard the replay uses.
function rmaRound(x, d) {
  const f = 10 ** d;
  return Math.sign(x) * Math.round(Math.abs(x) * f + 1e-9) / f;
}

// Endorsement quarter label "Q1 2026" -> "2026-Q1".
function quarterKeyOf(label) {
  const m = String(label || "").match(/Q([1-4])\s*[- ]?\s*(\d{4})/);
  if (m) return `${m[2]}-Q${m[1]}`;
  const k = String(label || "").match(/^(\d{4})-Q([1-4])$/);
  return k ? label : null;
}

// Postal state -> FIPS, as RMA's A00832 state code carries it.
const STATE_FIPS = {
  AL: "01", AK: "02", AZ: "04", AR: "05", CA: "06", CO: "08", CT: "09", DE: "10", FL: "12", GA: "13",
  HI: "15", ID: "16", IL: "17", IN: "18", IA: "19", KS: "20", KY: "21", LA: "22", ME: "23", MD: "24",
  MA: "25", MI: "26", MN: "27", MS: "28", MO: "29", MT: "30", NE: "31", NV: "32", NH: "33", NJ: "34",
  NM: "35", NY: "36", NC: "37", ND: "38", OH: "39", OK: "40", OR: "41", PA: "42", RI: "44", SC: "45",
  SD: "46", TN: "47", TX: "48", UT: "49", VT: "50", VA: "51", WA: "53", WV: "54", WI: "55", WY: "56",
};
function stateFipsOf(state) {
  const s = String(state || "").trim().toUpperCase();
  if (/^\d{2}$/.test(s)) return s;
  return STATE_FIPS[s] || null;
}

// Parse every A00832 release text (oldest first) into ONE row per milk yield ID.
// An ID's actual yield is published in a LATER release than the one that issued
// it, so the union over releases is what carries it; a release that lists an ID
// without an actual never erases one an earlier release published.
function parseMilkYieldReleases(releases) {
  const byId = new Map();
  const iso = (s) => (s && /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : null);
  const num = (s) => (s == null || String(s).trim() === "" ? null : Number(s));
  for (const { text } of releases || []) {
    for (const line of String(text || "").split(/\r?\n/)) {
      const f = line.split("|");
      if (f[0] !== "A00832") continue;
      const id = parseInt(f[2], 10);
      if (!Number.isFinite(id)) continue;
      const released = iso(f[8]);
      const act = num(f[6]);
      const cur = byId.get(id);
      const row = cur || {
        milk_yield_id: id,
        filing_date: iso(f[9]) || released,
        reinsurance_year: f[3] ? parseInt(f[3], 10) : null,
        state_code: f[4],
        expected_yield: num(f[5]),
        expected_yield_sd: num(f[7]),
        released_date: released,
        actual_yield: null,
        actual_released_date: null,
      };
      if (!cur) byId.set(id, row);
      // released_date = the LATEST release that listed the ID — so max() over a
      // reinsurance year is the newest release already ingested (the sync's watermark).
      if (released && (!row.released_date || released > row.released_date)) row.released_date = released;
      if (act != null && row.actual_yield == null) { row.actual_yield = act; row.actual_released_date = released; }
    }
  }
  return [...byId.values()].sort((a, b) => a.milk_yield_id - b.milk_yield_id);
}

// A00834 (ADMDrpActualPrice) -> one row per Actual Price ID, the quarter's actual
// Class III / IV RMA settled on. Like A00832, an ID is listed with blank prices
// when issued and re-listed with them once the quarter settles; the union keeps
// the published values.
function parseActualPriceReleases(releases) {
  const byId = new Map();
  const iso = (s) => (s && /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : null);
  const num = (s) => (s == null || String(s).trim() === "" ? null : Number(s));
  for (const { text } of releases || []) {
    for (const line of String(text || "").split(/\r?\n/)) {
      const f = line.split("|");
      if (f[0] !== "A00834") continue;
      const id = parseInt(f[2], 10);
      if (!Number.isFinite(id)) continue;
      const row = byId.get(id) || { actual_price_id: id, reinsurance_year: f[3] ? parseInt(f[3], 10) : null, actual_class_iii: null, actual_class_iv: null, released_date: null, price_released_date: null };
      byId.set(id, row);
      const rel = iso(f[22]);
      if (rel && (!row.released_date || rel > row.released_date)) row.released_date = rel; // latest listing (watermark)
      const iii = num(f[16]), iv = num(f[17]);
      if (iii != null && iv != null && row.actual_class_iii == null) { row.actual_class_iii = iii; row.actual_class_iv = iv; row.price_released_date = rel; }
    }
  }
  return [...byId.values()].sort((a, b) => a.actual_price_id - b.actual_price_id);
}

// The quarter's actual Class III/IV as RMA settles it — the simple average of the
// three USDA announced months, rounded to the cent — or null until all three of
// BOTH classes are announced. Input: drpGetQuarterActualsEnhanced's result.
function drpQuarterActualOf(actuals) {
  if (!actuals || actuals.monthsKnownIII !== 3 || actuals.monthsKnownIV !== 3) return null;
  if (actuals.classIII == null || actuals.classIV == null) return null;
  return { III: rmaRound(actuals.classIII, 2), IV: rmaRound(actuals.classIV, 2) };
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// When RMA should publish a quarter's actual yield: late in the month after the
// quarter ends (measured, see header). Returns {label, monthIso}.
function actualYieldDueOf(quarterKey) {
  const m = String(quarterKey || "").match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  let y = Number(m[1]), mon = Number(m[2]) * 3 + 1; // month after the quarter's last month
  if (mon > 12) { mon -= 12; y += 1; }
  return { label: `late ${MON[mon - 1]} ${y}`, monthIso: `${y}-${String(mon).padStart(2, "0")}` };
}

// Resolve the endorsement's RMA milk yield ID and its YAF.
//   end: { classIII, projectedPrice, effectiveDate (= sales date), policyState, quarter }
//   ctx: { priceRows: [{sales_date, milk_yield_id, quarter_code, exp_class_iii, exp_class_iv}],
//          yieldById: {id: {state_code, expected_yield, actual_yield, actual_released_date}},
//          actualPriceById: {quarter_code: {actual_class_iii, actual_class_iv}} (A00834),
//          quarterActual: drpQuarterActualOf(actuals) for THIS quarter, or null,
//          fallbackState, actualColumn: bool (false = sql/108 not applied yet) }
// Returns { status: "final"|"pending"|"unknown", yaf, milkYieldId, expectedYield,
//           actualYield, quarterKey, reason, due }
function drpResolveYieldAdj(end, ctx) {
  const c = ctx || {};
  const quarterKey = quarterKeyOf(end && end.quarter);
  const unknown = (reason, extra) => ({ status: "unknown", yaf: null, milkYieldId: null, expectedYield: null, actualYield: null, quarterKey, reason, due: null, ...(extra || {}) });
  const state = stateFipsOf((end && end.policyState) || c.fallbackState);
  if (!state) return unknown("policy state not known");
  const salesDate = String((end && end.effectiveDate) || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(salesDate)) return unknown("no sales date on the endorsement");
  const price = Number(end && end.projectedPrice);
  if (!(price > 0)) return unknown("no expected price on the endorsement");
  const isIII = end.classIII === 1 || end.classIII === true;
  const dayRows = (c.priceRows || []).filter((r) => String(r.sales_date).slice(0, 10) === salesDate);
  if (!dayRows.length) return unknown(`RMA's ${salesDate} price file is not loaded yet`);
  const yById = c.yieldById || {};
  const cents = (v) => (v == null || v === "" ? null : Math.round(Number(v) * 100));
  const hits = dayRows.filter((r) => {
    const y = yById[r.milk_yield_id];
    return y && String(y.state_code) === state && cents(isIII ? r.exp_class_iii : r.exp_class_iv) === Math.round(price * 100);
  });
  if (!hits.length) return unknown(`no RMA row for state ${state} on ${salesDate} at the endorsement's $${price.toFixed(2)} Class ${isIII ? "III" : "IV"} price`);
  // Two ADJACENT quarters can share a price to the cent on one sales date (measured:
  // Q1/Q2 2026 Class IV $19.38 on 2025-05-08). Resolved only by RMA's own data:
  //  1. yields identical across the matches -> the choice changes nothing;
  //  2. the quarter has SETTLED -> keep the match whose A00834 actual Class III AND
  //     IV (its Actual Price ID, A00833 field 43) equal this quarter's USDA average,
  //     rounded to the cent — the figure RMA settles on;
  //  3. no match has an actual yield yet -> pending (YAF 1 either way, no ID named);
  //  otherwise UNKNOWN.
  const actOf = (id) => { const v = yById[id] && yById[id].actual_yield; return v == null || v === "" ? null : Number(v); };
  const sig = (id) => `${Number(yById[id].expected_yield)}|${actOf(id) == null ? "" : actOf(id)}`;
  let chosen = hits;
  if (new Set(hits.map((h) => sig(h.milk_yield_id))).size > 1) {
    const qa = c.quarterActual, apById = c.actualPriceById || {};
    const cent = (v) => (v == null || v === "" ? null : Math.round(Number(v) * 100));
    if (qa && qa.III != null && qa.IV != null) {
      chosen = hits.filter((h) => { const a = apById[h.quarter_code];
        return a && cent(a.actual_class_iii) === cent(qa.III) && cent(a.actual_class_iv) === cent(qa.IV); });
    } else chosen = [];
    if (chosen.length !== 1) {
      if (hits.every((h) => actOf(h.milk_yield_id) == null)) {
        return { status: "pending", yaf: 1, milkYieldId: null, expectedYield: null, actualYield: null, quarterKey,
          reason: `${hits.length} RMA rows match the price; RMA has not published the actual yield`, due: actualYieldDueOf(quarterKey) };
      }
      return unknown(`${hits.length} RMA rows match the price with different yields — ambiguous until RMA's actual quarter price is loaded`);
    }
  }
  const id = chosen[0].milk_yield_id, y = yById[id];
  const exp = Number(y.expected_yield);
  if (!(exp > 0)) return unknown(`RMA expected yield missing for milk yield ID ${id}`, { milkYieldId: id });
  const act = y.actual_yield == null || y.actual_yield === "" ? null : Number(y.actual_yield);
  if (act == null || !(act > 0)) {
    return { status: "pending", yaf: 1, milkYieldId: id, expectedYield: exp, actualYield: null, quarterKey,
      reason: c.actualColumn === false ? "RMA actual yield not loaded yet" : "RMA has not published the actual yield",
      due: actualYieldDueOf(quarterKey) };
  }
  return { status: "final", yaf: rmaRound(act / exp, 4), milkYieldId: id, expectedYield: exp, actualYield: act, quarterKey, reason: null, due: null };
}

// The AIP's own revenue guarantee when it is on the row; else P28-1 from the
// expected price and coverage; else the trigger price (estimate only).
function drpRevenueGuarantee(end) {
  const lbs = Number(end && end.declaredMilkProd) || 0;
  const g = Number(end && end.expRevGuar);
  if (g > 0) return g;
  const p = Number(end && end.projectedPrice), cl = Number(end && end.coverageLevel);
  if (p > 0 && cl > 0) return rmaRound(rmaRound(rmaRound(p * lbs, 4) / 100, 0) * cl, 0);
  return (Number(end && end.triggerPrice) || 0) * lbs / 100;
}

// P28-1 indemnity for one endorsement. `settled` = all three months of the
// class announced: the quarter price is then RMA's (the average rounded to the
// cent, A00834) and every dollar step rounds as RMA's does. Otherwise the
// arithmetic is the same and unrounded (an estimate).
function drpP28Indemnity({ guarantee, lbs, share, protectionFactor, actualPrice, yaf, settled }) {
  const y = yaf == null ? 1 : yaf;
  const shr = share == null || share === "" ? 1 : (Number(share) > 1 ? Number(share) / 100 : Number(share) || 1);
  const pf = Number(protectionFactor) || 1;
  if (settled) {
    const ap = rmaRound(actualPrice, 2);
    const amr = rmaRound(rmaRound(ap * lbs * y, 4) / 100, 0);
    return rmaRound(Math.max(guarantee - amr, 0) * shr * pf, 0);
  }
  const amr = actualPrice * lbs * y / 100;
  return Math.max(guarantee - amr, 0) * shr * pf;
}

// Roll a quarter's endorsements (each already carrying .yieldAdj) into what the
// card prints: the lb-weighted YAF over endorsements RMA has FINALISED, how many
// are pending (with RMA's expected publish month) and how many are unknown.
function drpQuarterYieldSummary(rows) {
  let num = 0, lbs = 0, finalN = 0, pendingN = 0, unknownN = 0, due = null; const reasons = [];
  for (const e of rows || []) {
    const a = e && e.yieldAdj; const w = Number(e && e.declaredMilkProd) || 0;
    if (!a || a.status === "unknown") { unknownN += 1; if (a && a.reason) reasons.push(a.reason); continue; }
    if (a.status === "pending") { pendingN += 1; if (!due && a.due) due = a.due; continue; }
    finalN += 1; if (w > 0) { num += a.yaf * w; lbs += w; }
  }
  const total = finalN + pendingN + unknownN;
  return {
    total, finalN, pendingN, unknownN, due,
    yafAvg: lbs > 0 ? num / lbs : null,
    reasons: [...new Set(reasons)],
    pendingText: pendingN ? `Yield adjustment pending (RMA publishes ~${due ? due.label : "after the quarter"})` : null,
    unknownText: unknownN ? `${unknownN} of ${total} endorsement${total === 1 ? "" : "s"}: yield adjustment unknown — not applied` : null,
  };
}

module.exports = {
  rmaRound, quarterKeyOf, STATE_FIPS, stateFipsOf,
  parseMilkYieldReleases, parseActualPriceReleases, drpQuarterActualOf, actualYieldDueOf, drpResolveYieldAdj,
  drpRevenueGuarantee, drpP28Indemnity, drpQuarterYieldSummary,
};
